import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Wallet, Calendar, Info, FileText, History } from 'lucide-react';
import { api } from '../lib/api';
import { formatCurrency, formatDate } from '@/lib/format';
import { PAYMENT_METHODS } from '../lib/constants';
import { paymentBase } from '../lib/collectionLedger';
import CollectionModal from './CollectionModal';
import { JobDiscountForm, CancelJobDiscountModal } from './JobDiscountDialogs';
import InstallmentModal from './InstallmentModal';
import CollectionReceiptPreviewModal from './CollectionReceiptPreviewModal';
import CollectionReceiptsHistoryModal from './CollectionReceiptsHistoryModal';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';

export default function JobCollectionsTab({ jobId, job, isMobile }) {
    const [collections, setCollections] = useState([]);
    const [installments, setInstallments] = useState([]);
    const [receivable, setReceivable] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [colModalOpen, setColModalOpen] = useState(false);
    const [instModalOpen, setInstModalOpen] = useState(false);
    const [editCol, setEditCol] = useState(null);
    const [editInst, setEditInst] = useState(null);

    // Job discounts (İskonto)
    const [discounts, setDiscounts] = useState([]);
    const [cancelDiscount, setCancelDiscount] = useState(null);

    // Collection Receipt States
    const [receiptPreviewOpen, setReceiptPreviewOpen] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);
    const [issueConfirmOpen, setIssueConfirmOpen] = useState(false);
    const [targetCollectionForReceipt, setTargetCollectionForReceipt] = useState(null);
    const [issuingReceipt, setIssuingReceipt] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyCollection, setHistoryCollection] = useState(null);

    useEffect(() => {
        if (jobId) fetchData();
    // eslint-disable-next-line
    }, [jobId]);

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [cRes, iRes, rRes, dRes] = await Promise.all([
                api.get(`/collections/ledger?job_id=${jobId}`),
                api.get(`/jobs/${jobId}/installments`),
                api.get(`/jobs/${jobId}/receivables`),
                api.get(`/jobs/${jobId}/discounts`)
            ]);
            setDiscounts(Array.isArray(dRes?.data) ? dRes.data : []);
            setCollections(cRes.data);
            setInstallments(iRes.data);
            setReceivable(rRes.data);
        } catch (e) {
            setError("Tahsilat geçmişi yüklenemedi.");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmIssueReceipt = async () => {
        if (!targetCollectionForReceipt) return;
        setIssuingReceipt(true);
        try {
            const res = await api.post(`${paymentBase(targetCollectionForReceipt)}/receipt`);
            toast.success("Tahsilat makbuzu oluşturuldu.");
            await fetchData();
            setIssueConfirmOpen(false);
            setTargetCollectionForReceipt(null);
            setSelectedReceipt(res.data);
            setReceiptPreviewOpen(true);
        } catch (err) {
            const msg = err.response?.data?.detail || "Makbuz oluşturulamadı.";
            toast.error(msg);
        } finally {
            setIssuingReceipt(false);
        }
    };

    const handleDeleteCollection = async (id, amount, hasReceipts) => {
        if (hasReceipts) {
            toast.error("Makbuz geçmişi bulunan tahsilatlar silinemez. Denetim bütünlüğü için bu kayıt korunmaktadır.");
            return;
        }
        const confirmSil = window.prompt(`Bu tahsilatı SİLMEK istediğinize emin misiniz? İşlemi onaylamak için büyük harflerle SİL yazın.\nTutar: ${formatCurrency(amount)}`);
        if (confirmSil === 'SİL') {
            try {
                await api.delete(`/collections/${id}`);
                toast.success("Tahsilat silindi");
                fetchData();
            } catch (e) {
                const msg = e.response?.data?.detail || "Silme işlemi başarısız";
                toast.error(msg);
            }
        }
    };

    const handleDeleteInst = async (id, amount) => {
        const confirmSil = window.prompt(`Bu taksiti SİLMEK istediğinize emin misiniz? Taksite bağlı tahsilatlar serbest kalacaktır. İşlemi onaylamak için büyük harflerle SİL yazın.\nTutar: ${formatCurrency(amount)}`);
        if (confirmSil === 'SİL') {
            try {
                await api.delete(`/jobs/${jobId}/installments/${id}`);
                toast.success("Taksit silindi");
                fetchData();
            } catch (e) {
                toast.error("Silme işlemi başarısız");
            }
        }
    };

    return (
        <div className="space-y-6">
            {receivable && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-sm text-gray-500">Sözleşme Tutarı</p>
                            <p className="text-lg font-bold">{formatCurrency(receivable.sales_amount)}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-sm text-gray-500">Tahsil Edilen</p>
                            <p className="text-lg font-bold text-green-600">{formatCurrency(receivable.total_collected)}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-sm text-gray-500">İskonto</p>
                            <p className="text-lg font-bold text-purple-600" data-testid="job-total-discounts">{formatCurrency(receivable.total_discounts || 0)}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-sm text-gray-500">Kalan Alacak</p>
                            <p className="text-lg font-bold text-gp-red">{formatCurrency(receivable.remaining_receivable)}</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <p className="text-sm text-gray-500">Tahsilat Oranı</p>
                            <p className="text-lg font-bold text-gp-blue">%{parseFloat(receivable.collection_percentage || 0).toFixed(1)}</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Tahsilatlar Tablosu */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-green-600" /> Tahsilat Geçmişi
                        </CardTitle>
                        {!isMobile && !job.is_archived && (
                            <Button size="sm" variant="outline" onClick={() => { setEditCol(null); setColModalOpen(true); }}>
                                <Plus className="w-4 h-4 mr-1" /> Ekle
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Tarih</TableHead>
                                    <TableHead>Yöntem</TableHead>
                                    <TableHead className="text-right">Tutar</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {error ? (
                                    <TableRow><TableCell colSpan={4} role="alert">{error} <Button variant="outline" onClick={fetchData}>Tekrar Dene</Button></TableCell></TableRow>
                                ) : loading ? (
                                    <TableRow><TableCell colSpan={4}>Yükleniyor...</TableCell></TableRow>
                                ) : collections.length === 0 ? (
                                    <TableRow><TableCell colSpan={4} className="text-center py-4 text-gray-500">Kayıt yok</TableCell></TableRow>
                                ) : collections.map(c => (
                                    <TableRow key={c.id}>
                                        <TableCell>{formatDate(c.collection_date)}</TableCell>
                                        <TableCell>{PAYMENT_METHODS[c.payment_method] || c.payment_method}</TableCell>
                                        <TableCell className="text-right font-semibold">{formatCurrency(c.amount)}{c.status === 'cancelled' && <span className="block text-xs text-red-600">İptal edildi</span>}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                                {c.active_receipt_id && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-7 px-2 text-xs border-blue-200 text-blue-700 hover:bg-blue-50 font-medium"
                                                        onClick={() => {
                                                            setSelectedReceipt({
                                                                id: c.active_receipt_id,
                                                                receipt_number: c.active_receipt_number,
                                                                status: "issued",
                                                            });
                                                            setReceiptPreviewOpen(true);
                                                        }}
                                                        data-testid={`receipt-btn-${c.id}`}
                                                    >
                                                        <FileText className="w-3.5 h-3.5 mr-1 text-blue-600" />
                                                        Makbuz
                                                    </Button>
                                                )}
                                                {c.has_receipts && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                                        onClick={() => {
                                                            setHistoryCollection(c);
                                                            setHistoryModalOpen(true);
                                                        }}
                                                        title="Makbuz Geçmişi"
                                                        data-testid={`receipt-history-btn-${c.id}`}
                                                    >
                                                        <History className="w-3.5 h-3.5 mr-1 text-slate-500" />
                                                        Makbuzlar
                                                    </Button>
                                                )}
                                                {!c.active_receipt_id && c.status !== 'cancelled' && !isMobile && !job.is_archived && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 px-2 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                                        onClick={() => {
                                                            setTargetCollectionForReceipt(c);
                                                            setIssueConfirmOpen(true);
                                                        }}
                                                        data-testid={`create-receipt-btn-${c.id}`}
                                                    >
                                                        Makbuz Oluştur
                                                    </Button>
                                                )}
                                                {c.source === 'collection' && !isMobile && !job.is_archived && (
                                                    <>
                                                        {c.active_receipt_id ? (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="text-slate-300 p-0 h-auto cursor-not-allowed text-xs"
                                                                title="Bu tahsilata ait aktif bir makbuz bulunmaktadır. Düzenleme yapmadan önce makbuzu iptal edin."
                                                                onClick={() => toast.error("Bu tahsilata ait aktif bir makbuz bulunmaktadır. Düzenleme yapmadan önce makbuzu iptal edin.")}
                                                            >
                                                                Düzenle
                                                            </Button>
                                                        ) : (
                                                            <Button size="sm" variant="ghost" className="text-blue-600 p-0 h-auto text-xs" onClick={() => { setEditCol({ ...c, id: c.source_id }); setColModalOpen(true); }}>Düzenle</Button>
                                                        )}
                                                        <Button size="sm" variant="ghost" className="text-red-600 p-0 h-auto text-xs" onClick={() => handleDeleteCollection(c.source_id, c.amount, c.has_receipts)}>Sil</Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Ödeme Planı (Taksitler) Tablosu */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2 border-b">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-orange-600" /> Ödeme Planı
                        </CardTitle>
                        {!isMobile && !job.is_archived && (
                            <Button size="sm" variant="outline" onClick={() => { setEditInst(null); setInstModalOpen(true); }}>
                                <Plus className="w-4 h-4 mr-1" /> Planla
                            </Button>
                        )}
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Vade</TableHead>
                                    <TableHead className="text-right">Planlanan</TableHead>
                                    <TableHead className="text-right">Ödenen</TableHead>
                                    <TableHead>Durum</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {installments.length === 0 ? (
                                    <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-500">Ödeme planı oluşturulmamış</TableCell></TableRow>
                                ) : installments.map(i => (
                                    <TableRow key={i.id}>
                                        <TableCell className={i.status === 'Gecikti' ? 'text-gp-red font-medium' : ''}>{formatDate(i.due_date)}</TableCell>
                                        <TableCell className="text-right">{formatCurrency(i.planned_amount)}</TableCell>
                                        <TableCell className="text-right">{formatCurrency(i.paid_amount)}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                i.status === 'Ödendi' ? 'bg-green-100 text-green-700' :
                                                i.status === 'Gecikti' ? 'bg-red-100 text-gp-red' :
                                                i.status === 'Kısmi Ödendi' ? 'bg-blue-100 text-gp-blue' :
                                                'bg-gray-100 text-gray-600'
                                            }`}>{i.status}</span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {!isMobile && !job.is_archived && (
                                                <div className="flex justify-end gap-2">
                                                    <Button size="sm" variant="ghost" className="text-blue-600 p-0 h-auto" onClick={() => { setEditInst(i); setInstModalOpen(true); }}>Düzenle</Button>
                                                    <Button size="sm" variant="ghost" className="text-red-600 p-0 h-auto" onClick={() => handleDeleteInst(i.id, i.planned_amount)}>Sil</Button>
                                                </div>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>



            {/* İskonto Geçmişi */}
            <Card>
                <CardHeader className="pb-2 border-b">
                    <CardTitle className="text-base">İskonto Geçmişi</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Tarih</TableHead>
                                <TableHead>Gerekçe</TableHead>
                                <TableHead className="text-right">Tutar</TableHead>
                                <TableHead>Durum</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {discounts.length === 0 ? (
                                <TableRow><TableCell colSpan={5} className="text-center py-4 text-gray-500">İskonto kaydı yok</TableCell></TableRow>
                            ) : discounts.map(d => (
                                <TableRow key={d.id} data-testid={`discount-row-${d.id}`} className={d.status === 'cancelled' ? 'text-gray-400' : ''}>
                                    <TableCell>{formatDate(d.discount_date)}</TableCell>
                                    <TableCell>
                                        {d.reason}
                                        {d.status === 'cancelled' && d.cancellation_reason && (
                                            <span className="block text-xs text-red-600">İptal gerekçesi: {d.cancellation_reason}</span>
                                        )}
                                    </TableCell>
                                    <TableCell className={`text-right font-semibold ${d.status === 'cancelled' ? 'line-through' : ''}`}>{formatCurrency(d.amount)}</TableCell>
                                    <TableCell>{d.status === 'cancelled' ? 'İptal edildi' : 'Aktif'}</TableCell>
                                    <TableCell className="text-right">
                                        {d.status === 'active' && !isMobile && !job.is_archived && (
                                            <Button size="sm" variant="ghost" className="text-red-600 p-0 h-auto" onClick={() => setCancelDiscount(d)}>İptal</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {cancelDiscount && (
                <CancelJobDiscountModal
                    open={!!cancelDiscount}
                    onOpenChange={(o) => { if (!o) setCancelDiscount(null); }}
                    jobId={jobId}
                    discount={cancelDiscount}
                    onSuccess={fetchData}
                />
            )}

            {colModalOpen && (
                <CollectionModal
                    open={colModalOpen}
                    onOpenChange={setColModalOpen}
                    job={job}
                    existingData={editCol}
                    onSuccess={fetchData}
                >
                    {!editCol && (
                        <details className="border-t pt-3">
                            <summary className="cursor-pointer text-sm font-medium">İskonto</summary>
                            <JobDiscountForm
                                jobId={jobId}
                                remaining={receivable?.remaining_receivable}
                                onCancel={() => setColModalOpen(false)}
                                onSuccess={fetchData}
                            />
                        </details>
                    )}
                </CollectionModal>
            )}

            {instModalOpen && (
                <InstallmentModal
                    open={instModalOpen}
                    onOpenChange={setInstModalOpen}
                    jobId={jobId}
                    existingData={editInst}
                    onSuccess={fetchData}
                />
            )}

            {/* Receipt Issue Confirmation Dialog */}
            {issueConfirmOpen && (
                <Dialog open={issueConfirmOpen} onOpenChange={setIssueConfirmOpen}>
                    <DialogContent className="max-w-md w-[90vw] p-6 bg-white border border-[#DCE2E8] shadow-xl">
                        <DialogHeader className="pb-3 border-b border-[#DCE2E8]">
                            <div className="flex items-center gap-2 text-slate-900">
                                <FileText className="h-5 w-5 text-blue-600" />
                                <DialogTitle className="text-base font-semibold">
                                    Tahsilat Makbuzu Oluştur
                                </DialogTitle>
                            </div>
                            <DialogDescription className="text-xs text-slate-600 pt-2">
                                Makbuz oluşturulduktan sonra bu tahsilat, makbuz iptal edilmeden düzenlenemez.
                            </DialogDescription>
                        </DialogHeader>

                        <DialogFooter className="pt-3 flex items-center justify-end gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setIssueConfirmOpen(false);
                                    setTargetCollectionForReceipt(null);
                                }}
                                disabled={issuingReceipt}
                                className="text-xs h-9 border-slate-300"
                            >
                                Vazgeç
                            </Button>
                            <Button
                                type="button"
                                onClick={handleConfirmIssueReceipt}
                                disabled={issuingReceipt}
                                className="bg-[#168DEB] hover:bg-[#1272BE] text-white text-xs h-9 shadow-sm"
                                data-testid="confirm-issue-receipt-btn"
                            >
                                {issuingReceipt ? "Oluşturuluyor..." : "Makbuz Oluştur"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {/* Receipt Preview Modal */}
            {receiptPreviewOpen && selectedReceipt && (
                <CollectionReceiptPreviewModal
                    open={receiptPreviewOpen}
                    onOpenChange={setReceiptPreviewOpen}
                    receipt={selectedReceipt}
                    isMobile={isMobile}
                    onReceiptVoided={(updated) => {
                        setSelectedReceipt(updated);
                        fetchData();
                    }}
                />
            )}

            {/* Receipt History Modal */}
            {historyModalOpen && historyCollection && (
                <CollectionReceiptsHistoryModal
                    open={historyModalOpen}
                    onOpenChange={setHistoryModalOpen}
                    collection={historyCollection}
                    isMobile={isMobile}
                    onReceiptUpdated={fetchData}
                />
            )}
        </div>
    );
}
